import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  Maximize, 
  ChevronLeft, 
  ChevronRight,
  MessageCircle,
  Clock,
  User,
  Send,
  FileText,
  ClipboardCheck
} from 'lucide-react';
import { getCurrentUser } from '@/lib/auth';
import { 
  getVideos, 
  getCategories, 
  saveVideoProgress, 
  addToHistory, 
  getComments, 
  addComment, 
  deleteComment,
  updateVideo,
  getVideoProgress as getVideoProgressApi,
  getModules
} from '@/lib/storage';
import { useToast } from '@/hooks/use-toast';
import VimeoPlayer from '@/components/VimeoPlayer';
import PdfViewer from '@/components/PdfViewer';
import CourseBreadcrumb, { type TrilhaItem } from '@/components/CourseBreadcrumb';
import { formatDurationClock, formatDurationLong, isActualVideo, isReleased, isSupportMaterial } from '@/lib/utils';
import SobreAAula from '@/components/SobreAAula';

function extractVimeoId(url?: string | null): string | undefined {
  if (!url) return undefined;
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match?.[1];
}

export default function VideoPlayer() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const user = getCurrentUser();
  const videoRef = useRef<HTMLVideoElement>(null);
  
  const [video, setVideo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState<any[]>([]);
  const [modulosDaCategoria, setModulosDaCategoria] = useState<Array<{ id: string; title: string; parentId?: string | null }>>([]);
  const [videos, setVideos] = useState<any[]>([]);
  const [comments, setComments] = useState<any[]>([]);
  useEffect(() => {
    (async () => {
      // Limpar estados anteriores ao trocar de vídeo
      setVideo(null);
      setIsLoading(true);
      setIsCompleted(false);
      setMaxProgress(0);
      setProgress(0);
      setCurrentTime(0);
      setComments([]);
      setAutoNextCountdown(null);
      setAutoNextTarget(null);
      
      const [vList, cList] = await Promise.all([getVideos(), getCategories()]);
      setVideos(vList);
      setCategories(cList);
      const v = vList.find(v => v.id === videoId);
      if (v && user?.role !== 'admin') {
        const categoryIds: string[] = (v as any).categoryIds || (v as any).category_ids || [v.categoryId || (v as any).category_id].filter(Boolean);
        const moduleId = (v as any).moduleId || (v as any).module_id;
        const allowedByCategory = categoryIds.some((id) => (user?.assignedCategories || []).includes(id));
        const allowedByModule = moduleId ? (user?.assignedModules || []).includes(moduleId) : false;
        const categoriesReleased = categoryIds.some((id) => {
          const category = cList.find((c) => c.id === id);
          return !category || isReleased(category);
        });
        if (!isReleased(v) || !categoriesReleased || (!allowedByCategory && !allowedByModule)) {
          setVideo(null);
          setIsLoading(false);
          return;
        }
      }
      // Módulos da categoria, para montar a trilha de navegação.
      if (v?.categoryId) {
        try { setModulosDaCategoria(await getModules(v.categoryId)); } catch { setModulosDaCategoria([]); }
      } else {
        setModulosDaCategoria([]);
      }
      setVideo(v || null);
      if (v) {
        setEditTitle(v.title || '');
        setEditDescription(v.description || '');
        setEditCategoryId(v.categoryId || '');
        setEditThumbnail(v.thumbnail);
      }
      // Carregar progresso salvo para retomar do ponto
      const vp = await getVideoProgressApi(videoId || '');
      if (vp) {
        if (vp.completed) {
          setIsCompleted(true);
          setMaxProgress(100);
          setProgress(100);
          if (vp.currentTime > 0) setCurrentTime(vp.currentTime);
        } else if (vp.currentTime > 0 && vp.duration > 0) {
          const initial = Math.min(100, Math.max(0, (vp.currentTime / vp.duration) * 100));
          setMaxProgress(initial);
          setProgress(initial);
          setCurrentTime(vp.currentTime);
        }
      }
      setComments(await getComments(videoId || ''));
      setIsLoading(false);
    })();
  }, [videoId]);
  const [newComment, setNewComment] = useState('');
  const getInitials = (name?: string) => {
    const base = (name || '').trim();
    if (!base) return '?';
    const parts = base.split(' ').filter(Boolean);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [progress, setProgress] = useState(0);
  const [maxProgress, setMaxProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewRegistered, setViewRegistered] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const [maxWatchedSeconds, setMaxWatchedSeconds] = useState(0);
  const [lastVimeoSeconds, setLastVimeoSeconds] = useState(0);
  const [lastHtml5Seconds, setLastHtml5Seconds] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCategoryId, setEditCategoryId] = useState('');
  const [editThumbnail, setEditThumbnail] = useState<string | undefined>(undefined);
  const thumbInputRef = useRef<HTMLInputElement>(null);

  // Auto Next
  const [autoNextCountdown, setAutoNextCountdown] = useState<number | null>(null);
  const [autoNextTarget, setAutoNextTarget] = useState<any | null>(null);

  // Aulas da mesma pasta — ou, sem pasta, as soltas da mesma categoria — na
  // ordem definida no admin, com a data de envio desempatando. É a mesma ordem
  // em que o servidor devolve a lista e em que a pasta aparece para o aluno.
  // Antes o "Próximo vídeo" e os relacionados usavam só a data de envio.
  const sequenciaDaPasta = () => {
    if (!video) return [];
    const pasta = (video as any).moduleId || (video as any).module_id;
    return videos
      .filter((v) => {
        if (!isActualVideo(v)) return false;
        const p = (v as any).moduleId || (v as any).module_id;
        return pasta ? p === pasta : v.categoryId === video.categoryId && !p;
      })
      .sort((a, b) =>
        (Number(a.order ?? 0) - Number(b.order ?? 0)) ||
        (new Date(a.uploadedAt || 0).getTime() - new Date(b.uploadedAt || 0).getTime()));
  };

  const computeNextVideo = () => {
    if (!video) return null;
    const allVideosInModule = sequenciaDaPasta();
    
    // Encontrar o índice do vídeo atual na lista ordenada
    const currentIndex = allVideosInModule.findIndex(v => v.id === video.id);
    
    // Se não encontrou o vídeo atual ou é o último, retornar null
    if (currentIndex < 0 || currentIndex >= allVideosInModule.length - 1) {
      return null;
    }
    
    // Retornar o próximo vídeo na sequência
    return allVideosInModule[currentIndex + 1];
  };

  const startAutoNext = (seconds = 8) => {
    const next = computeNextVideo();
    if (!next) return;
    setAutoNextTarget(next);
    setAutoNextCountdown(Math.max(1, seconds));
  };

  const cancelAutoNext = () => {
    setAutoNextCountdown(null);
    setAutoNextTarget(null);
  };

  const goToAutoNext = () => {
    if (!autoNextTarget) return;
    const id = autoNextTarget.id;
    cancelAutoNext();
    navigate(`/video/${id}`);
  };

  useEffect(() => {
    if (autoNextCountdown === null) return;
    if (autoNextCountdown <= 0) {
      goToAutoNext();
      return;
    }
    const t = setTimeout(() => setAutoNextCountdown((s) => (s === null ? s : s - 1)), 1000);
    return () => clearTimeout(t);
  }, [autoNextCountdown]);

  const category = categories.find(c => c.id === video?.categoryId);
  
  // Obter moduleId do vídeo atual
  const currentModuleId = (video as any)?.moduleId || (video as any)?.module_id;

  // Trilha Categoria › Módulo › Submódulo › Aula. O breadcrumb anterior pulava
  // os módulos, então o usuário não sabia em que subpasta estava.
  const trilhaDeNavegacao = useMemo(() => {
    const itens: TrilhaItem[] = [];
    const categoria = categories.find((c) => c.id === (video?.categoryId || (video as any)?.category_id));
    if (categoria) itens.push({ label: categoria.name });

    const cadeia: string[] = [];
    let atual = currentModuleId ? modulosDaCategoria.find((m) => m.id === currentModuleId) : null;
    const vistos = new Set<string>();
    while (atual && !vistos.has(atual.id)) {
      cadeia.unshift(atual.title);
      vistos.add(atual.id);
      atual = atual.parentId ? modulosDaCategoria.find((m) => m.id === atual!.parentId) : null;
    }
    for (const titulo of cadeia) itens.push({ label: titulo });

    if (video?.title) itens.push({ label: video.title });
    return itens;
  }, [categories, video, currentModuleId, modulosDaCategoria]);
  
  // Vídeos relacionados do mesmo módulo (ou categoria se não houver módulo)
  // Relacionados = as próximas aulas a partir da atual. Na última aula da
  // pasta, as anteriores — em vez de um quadro vazio ou das 5 primeiras, que
  // o aluno provavelmente já assistiu.
  const sequencia = sequenciaDaPasta();
  const posicaoAtual = sequencia.findIndex((v) => v.id === video?.id);
  const seguintes = posicaoAtual >= 0
    ? sequencia.slice(posicaoAtual + 1)
    : sequencia.filter((v) => v.id !== video?.id);
  const relacionadosSaoProximos = seguintes.length > 0;
  const relatedVideos = relacionadosSaoProximos
    ? seguintes
    : sequencia.slice(0, Math.max(0, posicaoAtual)).reverse();
  
  // Materiais de apoio do mesmo módulo (ou categoria se não houver módulo)
  const supportMaterials = videos
    .filter(v => {
      if (!isSupportMaterial(v)) return false;
      
      const vModuleId = (v as any).moduleId || (v as any).module_id;
      
      // Se o vídeo atual tem módulo, filtrar pelo mesmo módulo
      if (currentModuleId) {
        return vModuleId === currentModuleId;
      }
      
      // Fallback: usar categoria
      return v.categoryId === video?.categoryId;
    })
    .sort((a, b) => {
      const dateA = new Date(a.uploadedAt || 0).getTime();
      const dateB = new Date(b.uploadedAt || 0).getTime();
      return dateA - dateB;
    });
  
  const vimeoIdFromUrl = extractVimeoId(video?.videoUrl);

  // Detectar Content-Type da URL (útil para /api/files/:id)
  const [contentType, setContentType] = useState<string | null>(null);
  useEffect(() => {
    setContentType(null);
    const url = video?.videoUrl || '';
    if (!url) return;
    let aborted = false;
    (async () => {
      try {
        const resp = await fetch(url, { method: 'HEAD' });
        if (!aborted) {
          setContentType(resp.headers.get('content-type'));
        }
      } catch {}
    })();
    return () => { aborted = true; };
  }, [video?.videoUrl]);

  const lowerUrl = String(video?.videoUrl || '').toLowerCase();
  const isPdf = (contentType?.startsWith('application/pdf') || lowerUrl.endsWith('.pdf')) || false;
  const isImage = (contentType?.startsWith('image/') || /\.(jpg|jpeg|png)$/i.test(lowerUrl)) || false;
  const isFileContent = (video?.contentType || (video as any)?.content_type) === 'file' || lowerUrl.includes('/api/files/');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!video) return;

    // Registrar visualização após 3 segundos de exibição
    const viewTimer = setTimeout(() => {
      if (!viewRegistered) {
        addToHistory({
          userId: user.id,
          videoId: video.id,
          watchedDuration: 0,
          completed: false,
          lastWatchedAt: new Date(),
        });
        setViewRegistered(true);
        
        toast({
          title: "Visualização registrada",
          description: "Sua visualização foi contabilizada.",
        });
      }
    }, 3000);

    return () => clearTimeout(viewTimer);
  }, [video, user, navigate, viewRegistered, toast]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleTimeUpdate = () => {
      const cur = videoElement.currentTime;
      setCurrentTime(cur);
      const computed = (cur / (videoElement.duration || duration || 1)) * 100;
      const clamped = Math.min(100, Math.max(0, computed));
      setMaxProgress((prev) => Math.max(prev, clamped, isCompleted ? 100 : 0));

      // Só aumenta progresso visual e tempo assistido se não for seek (avanço brusco)
      if (!isSeeking) {
        const delta = cur - lastHtml5Seconds;
        if (delta >= 0 && delta <= 1.5) {
          const nextWatched = Math.max(maxWatchedSeconds, Math.floor(cur));
          setMaxWatchedSeconds(nextWatched);
          const based = (nextWatched / Math.max(1, videoElement.duration || duration || 1)) * 100;
          setProgress((prev) => Math.max(prev, Math.min(100, Math.max(0, based))));
        }
      }
      setLastHtml5Seconds(cur);

      // Salvar progresso a cada 5 segundos sem regredir
      if (!isSeeking && Math.floor(cur) % 5 === 0) {
        saveProgress();
      }
    };

    const handleLoadedMetadata = async () => {
      setDuration(videoElement.duration);
      // Atualiza duração no backend se estiver desatualizada
      if (video && Math.floor(videoElement.duration || 0) > 0 && Math.abs((video.duration || 0) - Math.floor(videoElement.duration)) > 1) {
        try {
          await updateVideo({ ...(video as any), duration: Math.floor(videoElement.duration) } as any);
          setVideo((prev: any) => prev ? { ...prev, duration: Math.floor(videoElement.duration) } : prev);
        } catch {}
      }
      // Retomar posição salva se houver
      try {
        const vp = await getVideoProgressApi(videoId || '');
        if (vp && vp.currentTime > 0) {
          videoElement.currentTime = Math.max(0, Math.min(videoElement.duration - 1, vp.currentTime));
          setCurrentTime(videoElement.currentTime);
          const computedLoaded = (videoElement.currentTime / (videoElement.duration || 1)) * 100;
          const clampedLoaded = Math.min(100, Math.max(0, computedLoaded));
          setMaxProgress((prev) => Math.max(prev, clampedLoaded, isCompleted ? 100 : 0));
          setProgress((prev) => Math.max(prev, clampedLoaded, isCompleted ? 100 : 0));
        }
      } catch {}
    };

    const handleEnded = () => {
      setIsPlaying(false);
      if (user && video) {
        setIsCompleted(true);
        setMaxProgress(100);
        setProgress(100);
        addToHistory({
          userId: user.id,
          videoId: video.id,
          watchedDuration: video.duration,
          completed: true,
          lastWatchedAt: new Date(),
        });
        toast({
          title: "Vídeo concluído!",
          description: "Parabéns por completar este vídeo.",
        });
        // Iniciar contagem para próximo vídeo (somente se for vídeo real)
        if (isActualVideo(video)) {
          startAutoNext();
        }
      }
    };

    const handleSeeking = () => setIsSeeking(true);
    const handleSeeked = () => setIsSeeking(false);

    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoElement.addEventListener('ended', handleEnded);
    videoElement.addEventListener('seeking', handleSeeking);
    videoElement.addEventListener('seeked', handleSeeked);

    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.removeEventListener('ended', handleEnded);
      videoElement.removeEventListener('seeking', handleSeeking);
      videoElement.removeEventListener('seeked', handleSeeked);
    };
  }, [user, video, toast, videoId, duration, isCompleted, isSeeking]);

  // Removido incremento artificial de progresso para Vimeo; agora apenas eventos reais disparam salvamento

  const saveProgress = () => {
    if (!video || !user || !videoRef.current) return;
    
    saveVideoProgress({
      videoId: video.id,
      currentTime: videoRef.current.currentTime,
      duration: Math.max(video.duration || 0, videoRef.current.duration || 0),
      completed: false,
    });

    addToHistory({
      userId: user.id,
      videoId: video.id,
      watchedDuration: Math.floor(videoRef.current.currentTime),
      completed: false,
      lastWatchedAt: new Date(),
    });
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSkipBack = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10);
  };

  const handleSkipForward = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.min(duration, videoRef.current.currentTime + 10);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    videoRef.current.currentTime = percentage * duration;
  };

  const handleFullscreen = () => {
    if (!videoRef.current) return;
    if (videoRef.current.requestFullscreen) {
      videoRef.current.requestFullscreen();
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !user) return;
    try {
      const created = await addComment({
        videoId: videoId || '',
        userId: user.id,
        userName: user.name,
        content: newComment,
      });
      // Garantir userName no estado local
      const withName = { ...created, userName: user.name } as any;
      setComments([...comments, withName]);
      setNewComment('');
      toast({
        title: "Comentário adicionado",
        description: "Seu comentário foi publicado com sucesso.",
      });
    } catch (e) {
      toast({
        title: 'Erro ao comentar',
        description: 'Não foi possível publicar seu comentário.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card className="text-center py-12">
            <CardContent>
              <p className="text-muted-foreground">Carregando vídeo...</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (!video) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card className="text-center py-12">
            <CardContent>
              <p className="text-muted-foreground">Vídeo não encontrado.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        <CourseBreadcrumb itens={trilhaDeNavegacao} />

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Video Area */}
          <div className="lg:col-span-2 space-y-4">
            {/* Video Player */}
            <Card className="overflow-hidden">
              <div className="relative bg-black aspect-video">
                {isPdf ? (
                  <div className="absolute inset-0 w-full h-full">
                    <PdfViewer url={video.videoUrl} title={video.title} className="w-full h-full" />
                  </div>
                ) : isImage ? (
                  <img src={video.videoUrl} alt={video.title} className="absolute inset-0 w-full h-full object-contain bg-black" />
                ) : isFileContent ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-muted text-center p-6">
                    <FileText className="h-12 w-12 text-primary" />
                    <div>
                      <h3 className="font-semibold text-lg">Material disponível para consulta</h3>
                      <p className="text-sm text-muted-foreground">Este tipo de arquivo deve ser aberto em uma nova aba.</p>
                    </div>
                    <Button asChild>
                      <a href={video.videoUrl} target="_blank" rel="noreferrer">Abrir arquivo</a>
                    </Button>
                  </div>
                ) : (video.vimeoEmbedUrl || video.vimeoId || vimeoIdFromUrl) ? (
                  <VimeoPlayer
                    vimeoId={video.vimeoId || vimeoIdFromUrl}
                    vimeoEmbedUrl={video.vimeoEmbedUrl}
                    title={video.title}
                    startAtSeconds={currentTime}
                    onProgress={(progressPercent, vimeoDuration) => {
                      const clamped = Math.min(100, Math.max(0, progressPercent));
                      setMaxProgress((prev) => Math.max(prev, clamped, isCompleted ? 100 : 0));
                      setProgress((prev) => Math.max(prev, clamped, isCompleted ? 100 : 0));
                      setDuration(vimeoDuration);
                      // atualiza duração do vídeo se preciso
                      if (video && Math.floor(vimeoDuration || 0) > 0 && Math.abs((video.duration || 0) - Math.floor(vimeoDuration)) > 1) {
                        (async () => {
                          try {
                            await updateVideo({ ...(video as any), duration: Math.floor(vimeoDuration) } as any);
                            setVideo((prev: any) => prev ? { ...prev, duration: Math.floor(vimeoDuration) } : prev);
                          } catch {}
                        })();
                      }
                      // Apenas persiste progresso real; não marca como concluído por seek
                      if (user && video) {
                        const watchedDuration = Math.floor((clamped / 100) * vimeoDuration);
                        // evita regressão
                        setMaxWatchedSeconds((prev) => Math.max(prev, watchedDuration));
                        const safeWatched = Math.max(maxWatchedSeconds, watchedDuration);
                        saveVideoProgress({
                          videoId: video.id,
                          currentTime: safeWatched,
                          duration: vimeoDuration,
                          completed: isCompleted || clamped >= 95,
                        });
                        addToHistory({
                          userId: user.id,
                          videoId: video.id,
                          watchedDuration: safeWatched,
                          completed: isCompleted || clamped >= 95,
                          lastWatchedAt: new Date(),
                        });
                      }
                    }}
                    onComplete={() => {
                      if (user && video) {
                        setIsCompleted(true);
                        setMaxProgress(100);
                        setProgress(100);
                        addToHistory({
                          userId: user.id,
                          videoId: video.id,
                          watchedDuration: video.duration,
                          completed: true,
                          lastWatchedAt: new Date(),
                        });
                        toast({
                          title: "Vídeo concluído!",
                          description: "Parabéns por completar este vídeo.",
                        });
                        // Iniciar contagem para próximo vídeo (somente se for vídeo real)
                        if (isActualVideo(video)) {
                          startAutoNext();
                        }
                      }
                    }}
                    onPlay={() => {
                      if (!viewRegistered && user && video) {
                        addToHistory({
                          userId: user.id,
                          videoId: video.id,
                          watchedDuration: 0,
                          completed: false,
                          lastWatchedAt: new Date(),
                        });
                        setViewRegistered(true);
                      }
                    }}
                    className="w-full h-full"
                  />
                ) : (
                  <>
                    <video
                      ref={videoRef}
                      src={video.videoUrl}
                      className="w-full h-full"
                      onClick={togglePlay}
                    />
                    
                    {/* Video Controls */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                      {/* Progress Bar */}
                      <div 
                        className="w-full h-1 bg-white/30 rounded-full cursor-pointer mb-3"
                        onClick={handleProgressClick}
                      >
                        <div 
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-white hover:bg-white/20"
                            onClick={togglePlay}
                          >
                            {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                          </Button>
                          
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-white hover:bg-white/20"
                            onClick={handleSkipBack}
                          >
                            <SkipBack className="h-4 w-4" />
                          </Button>
                          
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-white hover:bg-white/20"
                            onClick={handleSkipForward}
                          >
                            <SkipForward className="h-4 w-4" />
                          </Button>
                          
                          <span className="text-white text-sm">
                            {formatDurationClock(currentTime)} / {formatDurationClock(duration)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Volume2 className="h-4 w-4 text-white" />
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={volume}
                            onChange={handleVolumeChange}
                            className="w-20"
                          />
                          
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-white hover:bg-white/20"
                            onClick={handleFullscreen}
                          >
                            <Maximize className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Card>

            {/* Video Info */}
            <Card>
              <CardHeader>
                {/* Banner de auto-next - só aparece para vídeos reais */}
                {autoNextTarget && autoNextCountdown !== null && isActualVideo(video) && (
                  <div className="mb-3">
                    <div className="rounded-md bg-primary/10 border border-primary/20 p-3 flex items-center justify-between">
                      <div className="text-sm">
                        Próximo vídeo em {autoNextCountdown}s: <span className="font-medium">{autoNextTarget.title}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={cancelAutoNext}>Cancelar</Button>
                        <Button size="sm" onClick={goToAutoNext}>Ir agora</Button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-start justify-between">
                  <div className="flex-1 pr-4">
                    {isEditing && user?.role === 'admin' ? (
                      <div className="space-y-2">
                        <Input
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          placeholder="Título do vídeo"
                        />
                        <Textarea
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          rows={3}
                          placeholder="Descrição do vídeo"
                        />
                      </div>
                    ) : (
                      <CardTitle className="text-2xl">{video.title}</CardTitle>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isEditing && user?.role === 'admin' ? (
                      <div className="min-w-[220px]">
                        <Select value={editCategoryId} onValueChange={setEditCategoryId}>
                          <SelectTrigger className="w-[220px]">
                            <SelectValue placeholder="Categoria" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <Badge>{category?.name}</Badge>
                    )}
                    {user?.role === 'admin' && (
                      isEditing ? (
                        <>
                          <Button
                            size="sm"
                            onClick={async () => {
                              if (!video) return;
                              try {
                                await updateVideo({
                                  ...video,
                                  title: editTitle,
                                  description: editDescription,
                                  categoryId: editCategoryId || video.categoryId,
                                  thumbnail: editThumbnail,
                                } as any);
                                setVideo({ ...video, title: editTitle, description: editDescription, categoryId: editCategoryId || video.categoryId, thumbnail: editThumbnail });
                                setIsEditing(false);
                                toast({ title: 'Vídeo atualizado' });
                              } catch (e) {
                                toast({ title: 'Erro ao salvar', variant: 'destructive' });
                              }
                            }}
                          >
                            Salvar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditTitle(video.title || '');
                              setEditDescription(video.description || '');
                              setEditCategoryId(video.categoryId || '');
                              setEditThumbnail(video.thumbnail);
                              setIsEditing(false);
                            }}
                          >
                            Cancelar
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                          Editar
                        </Button>
                      )
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {formatDurationLong(video.duration)}
                  </span>
                  <span>
                    Enviado em {new Date(video.uploadedAt).toLocaleDateString('pt-BR')}
                  </span>
                  {/* Botão Próximo vídeo - só aparece para vídeos reais, não para PDFs */}
                  {isActualVideo(video) && (
                    <Button
                      size="sm"
                      onClick={() => {
                        const next = computeNextVideo();
                        if (next) {
                          navigate(`/video/${next.id}`);
                        } else {
                          toast({
                            title: "Fim da sequência",
                            description: "Não há mais vídeos nesta categoria.",
                          });
                        }
                      }}
                    >
                      Próximo vídeo
                    </Button>
                  )}
                  {user?.role === 'admin' && isEditing && (
                    <div className="ml-auto flex items-center gap-3">
                      <input
                        ref={thumbInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setEditThumbnail(reader.result as string);
                          };
                          reader.readAsDataURL(file);
                        }}
                      />
                      <Button size="sm" variant="outline" onClick={() => thumbInputRef.current?.click()}>
                        Alterar thumbnail
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Atividade da aula (itens a/b do 2º documento). Fica logo abaixo do
                player, em destaque, porque é o passo seguinte esperado do aluno. */}
            {(video.hasForm || (video as any).has_form) && (() => {
              const link = video.formUrl || (video as any).form_url;
              const arquivo = video.formFile || (video as any).form_file;
              const destino = link || arquivo?.url;
              if (!destino) return null;
              return (
                <Card className="border-primary/40 bg-primary/5">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <ClipboardCheck className="h-5 w-5 text-primary" />
                      Atividade desta aula
                    </CardTitle>
                    <CardDescription>
                      {isCompleted
                        ? 'Você concluiu a aula. Responda a atividade para finalizar.'
                        : 'Ao terminar o vídeo, responda a atividade relacionada ao conteúdo.'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      className="gap-2"
                      onClick={() => window.open(destino, '_blank', 'noopener,noreferrer')}
                    >
                      <ClipboardCheck className="h-4 w-4" />
                      {arquivo && !link ? `Abrir ${arquivo.filename}` : 'Responder a atividade'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Ficha da aula: "Sobre o mentor" e "Sobre a aula" como subtópicos,
                com os materiais numa aba ao lado — formato pedido pelo cliente. */}
            <SobreAAula
              mentorName={video.mentorName || (video as any).mentor_name}
              mentorBio={video.mentorBio || (video as any).mentor_bio}
              mentorPhoto={video.mentorPhoto || (video as any).mentor_photo}
              descricao={video.description}
              materiais={(video.supportFiles || (video as any).support_files || []) as any}
            />

            {/* Comments Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="h-5 w-5" />
                  Comentários ({comments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add Comment */}
                <div className="flex gap-3">
                  <Avatar>
                    <AvatarFallback>
                      {user?.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-2">
                    <Textarea
                      placeholder="Adicione um comentário..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      rows={3}
                    />
                    <div className="flex justify-end">
                      <Button 
                        onClick={handleAddComment}
                        disabled={!newComment.trim()}
                        className="gap-2"
                      >
                        <Send className="h-4 w-4" />
                        Enviar
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Comments List */}
                <ScrollArea className="h-[400px]">
                  <div className="space-y-4">
                    {comments.map(comment => (
                      <div key={comment.id} className="flex gap-3">
                        <Avatar>
                          <AvatarFallback>
                            {getInitials((comment as any).userName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{(comment as any).userName || 'Usuário'}</span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(comment.createdAt).toLocaleDateString('pt-BR')}
                            </span>
                            {/* Admin delete */}
                            {user?.role === 'admin' && (
                              <button
                                onClick={async () => {
                                  try {
                                    await deleteComment(comment.id);
                                    setComments((prev) => prev.filter((c) => c.id !== comment.id));
                                    toast({ title: 'Comentário removido' });
                                  } catch (e) {
                                    toast({ title: 'Erro ao excluir comentário', variant: 'destructive' });
                                  }
                                }}
                                className="ml-2 text-xs text-destructive hover:underline"
                              >
                                excluir
                              </button>
                            )}
                          </div>
                          <p className="text-sm mt-1">{comment.content}</p>
                        </div>
                      </div>
                    ))}
                    {comments.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">
                        Seja o primeiro a comentar!
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Progress Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Seu Progresso</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Progress value={progress} />
                <p className="text-sm text-muted-foreground">
                  {Math.round(progress)}% concluído
                </p>
              </CardContent>
            </Card>

            {/* Sem nada para mostrar, o quadro some — antes ficava só o título. */}
            {relatedVideos.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {relacionadosSaoProximos ? 'Próximas aulas' : 'Aulas anteriores desta pasta'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {relatedVideos.slice(0, 5).map(relatedVideo => (
                  <div 
                    key={relatedVideo.id}
                    className="flex gap-3 cursor-pointer hover:bg-accent/50 p-2 rounded-md transition-colors"
                    onClick={() => navigate(`/video/${relatedVideo.id}`)}
                  >
                    <div className="aspect-video w-32 flex-shrink-0">
                      {relatedVideo.thumbnail ? (
                        <img 
                          src={relatedVideo.thumbnail} 
                          alt={relatedVideo.title}
                          className="w-full h-full object-cover rounded"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center rounded">
                          <Play className="h-6 w-6 text-primary/50" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium line-clamp-2">{relatedVideo.title}</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDurationLong(relatedVideo.duration)}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
            )}

            {/* Support Materials */}
            {supportMaterials.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Materiais de Apoio</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {supportMaterials.map(material => (
                    <div 
                      key={material.id}
                      className="flex gap-3 cursor-pointer hover:bg-accent/50 p-2 rounded-md transition-colors"
                      onClick={() => navigate(`/video/${material.id}`)}
                    >
                      <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-br from-orange-500/20 to-orange-500/10 flex items-center justify-center rounded">
                        <FileText className="h-6 w-6 text-orange-500/70" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium line-clamp-2">{material.title}</h4>
                        <p className="text-xs text-muted-foreground mt-1">
                          PDF
                        </p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
